'use client';
import { useEffect } from 'react';
import { usePathname, useSearchParams } from 'next/navigation';

// Initialize Google Analytics
export function useGoogleAnalytics() {
  const pathname = usePathname();
  const searchParams = useSearchParams();

  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;
    
    const measurementId = process.env.NEXT_PUBLIC_GA_MEASUREMENT_ID;
    if (!measurementId) return;

    // Initialize gtag if not already loaded
    if (!window.gtag) {
      const script = document.createElement('script');
      script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`;
      script.async = true;
      document.head.appendChild(script);

      window.dataLayer = window.dataLayer || [];
      window.gtag = function(...args: any[]) { window.dataLayer.push(args); };
      window.gtag('js', new Date());
      window.gtag('config', measurementId, {
        page_path: pathname,
      });
    }

    // Track page view on route change
    const url = pathname + (searchParams.toString() ? `?${searchParams}` : '');
    window.gtag('event', 'page_view', {
      page_path: url,
      page_title: document.title,
    });
  }, [pathname, searchParams]);
}

// Track custom events
export function trackEvent(eventName: string, eventParams: Record<string, any> = {}) {
  if (process.env.NODE_ENV === 'production' && window.gtag) {
    window.gtag('event', eventName, eventParams);
  }
}

// Track CTA button clicks
export function trackCTAClick(ctaName: string, ctaLocation: string) {
  trackEvent('cta_click', {
    cta_name: ctaName,
    cta_location: ctaLocation,
    page_path: window.location.pathname,
  });
}

// Track scroll depth
export function useScrollDepthTracking() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const thresholds = [25, 50, 75, 90, 100];
    const triggered = new Set<number>();

    const handleScroll = () => {
      const scrollHeight = document.documentElement.scrollHeight - window.innerHeight;
      const scrolled = window.scrollY;
      const scrollPercent = Math.round((scrolled / scrollHeight) * 100);

      thresholds.forEach((threshold) => {
        if (scrollPercent >= threshold && !triggered.has(threshold)) {
          triggered.add(threshold);
          trackEvent('scroll_depth', {
            scroll_depth: threshold,
            page_path: window.location.pathname,
          });
        }
      });
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
}

// Track user engagement time
export function useEngagementTracking() {
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') return;

    const startTime = Date.now();
    let isActive = true;

    const handleVisibilityChange = () => {
      isActive = !document.hidden;
    };

    const trackEngagement = () => {
      if (isActive) {
        const timeSpent = Math.round((Date.now() - startTime) / 1000);
        trackEvent('user_engagement', {
          engagement_time_seconds: timeSpent,
          page_path: window.location.pathname,
        });
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    
    // Track engagement every 30 seconds
    const interval = setInterval(trackEngagement, 30000);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      clearInterval(interval);
      trackEngagement(); // Final tracking on unmount
    };
  }, []);
}

// TypeScript declarations
declare global {
  interface Window {
    gtag: (...args: any[]) => void;
    dataLayer: any[];
  }
}
